class OishiMenuDrawer extends HTMLElement {
  connectedCallback() {
    if (this.dataset.menuReady === 'true') return;
    this.dataset.menuReady = 'true';

    this.trigger = this.querySelector('[data-menu-trigger]');
    this.layer = this.querySelector('[data-menu-layer]');
    this.drawer = this.querySelector('[data-menu-drawer]');
    this.siteHeader = this.querySelector('header');
    this.closeControls = Array.from(this.querySelectorAll('[data-menu-close]'));

    this.querySelectorAll('[data-country-selector]').forEach((select) => {
      select.addEventListener('change', () => select.form?.submit());
    });

    if (!this.trigger || !this.layer || !this.drawer) return;

    this.handleKeydown = this.handleKeydown.bind(this);
    this.trigger.addEventListener('click', () => this.openMenu());
    this.closeControls.forEach((control) => {
      control.addEventListener('click', () => this.closeMenu());
    });
    this.drawer.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', () => this.closeMenu({ restoreFocus: false }));
    });
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.handleKeydown);
    window.clearTimeout(this.closeTimer);
    document.body.classList.remove('menu-drawer-open');
  }

  openMenu() {
    window.clearTimeout(this.closeTimer);
    this.previouslyFocused = document.activeElement;
    this.layer.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
    this.trigger.setAttribute('aria-label', 'Close menu');
    this.siteHeader?.setAttribute('inert', '');
    document.body.classList.add('menu-drawer-open');
    document.addEventListener('keydown', this.handleKeydown);

    window.requestAnimationFrame(() => {
      this.layer.classList.add('is-open');
      this.querySelector('[data-menu-close]')?.focus();
    });
  }

  closeMenu({ restoreFocus = true } = {}) {
    if (this.layer.hidden) return;
    this.layer.classList.remove('is-open');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.trigger.setAttribute('aria-label', 'Open menu');
    this.siteHeader?.removeAttribute('inert');
    document.body.classList.remove('menu-drawer-open');
    document.removeEventListener('keydown', this.handleKeydown);

    this.closeTimer = window.setTimeout(() => {
      this.layer.hidden = true;
      if (restoreFocus && this.previouslyFocused instanceof HTMLElement) this.previouslyFocused.focus();
    }, 300);
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeMenu();
      return;
    }

    if (event.key !== 'Tab') return;
    const focusable = Array.from(
      this.drawer.querySelectorAll('a[href], button:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])')
    ).filter((element) => !element.hidden && element.getClientRects().length);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

customElements.define('oishi-menu-drawer', OishiMenuDrawer);

class OishiDisclosure extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll('[data-disclosure-trigger]').forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const panel = trigger.nextElementSibling;
        const isOpen = trigger.getAttribute('aria-expanded') === 'true';
        trigger.setAttribute('aria-expanded', String(!isOpen));
        if (panel) panel.hidden = isOpen;
      });
    });
  }
}

customElements.define('oishi-disclosure', OishiDisclosure);

class OishiMushroomGuide extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-mushroom-slide]'));
    this.tabs = Array.from(this.querySelectorAll('[data-mushroom-tab]'));
    this.index = 0;

    if (!this.slides.length || !this.tabs.length) return;

    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => {
        this.show(index);
        this.startTimer();
      });
    });

    this.show(0);
    this.startTimer();
  }

  disconnectedCallback() {
    window.clearInterval(this.timer);
  }

  startTimer() {
    window.clearInterval(this.timer);
    if (this.slides.length < 2) return;
    this.timer = window.setInterval(() => this.show((this.index + 1) % this.slides.length), 10000);
  }

  show(index) {
    this.index = index;
    this.slides.forEach((slide, slideIndex) => {
      const active = slideIndex === index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', String(!active));
    });
    this.tabs.forEach((tab, tabIndex) => {
      const active = tabIndex === index;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', String(active));
    });
  }
}

customElements.define('oishi-mushroom-guide', OishiMushroomGuide);

class OishiBrandDetails extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-brand-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-brand-dot]'));
    this.index = 0;

    if (!this.slides.length) return;

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.show(index);
        this.startTimer();
      });
    });

    this.show(0);
    this.startTimer();
  }

  disconnectedCallback() {
    window.clearInterval(this.timer);
  }

  startTimer() {
    window.clearInterval(this.timer);
    if (this.slides.length < 2) return;
    this.timer = window.setInterval(() => this.show((this.index + 1) % this.slides.length), 10000);
  }

  show(index) {
    this.index = index;
    this.slides.forEach((slide, slideIndex) => {
      const active = slideIndex === index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', String(!active));
    });
    this.dots.forEach((dot, dotIndex) => {
      const active = dotIndex === index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-selected', String(active));
    });
  }
}

customElements.define('oishi-brand-details', OishiBrandDetails);

class OishiLiveBanner extends HTMLElement {
  connectedCallback() {
    // The banner is a CSS marquee. Keeping this element as a hook makes the
    // section easy to enhance in Shopify without adding competing timers.
  }
}

customElements.define('oishi-live-banner', OishiLiveBanner);

class OishiPromisePills extends HTMLElement {
  connectedCallback() {
    if (this.dataset.promiseReady === 'true') return;
    this.dataset.promiseReady = 'true';

    this.buttons = Array.from(this.querySelectorAll('[data-promise-pill]'));
    this.detail = this.querySelector('[data-promise-detail]');
    if (!this.buttons.length || !this.detail) return;

    this.buttons.forEach((button) => {
      button.addEventListener('click', () => this.select(button));
    });
  }

  select(selectedButton) {
    this.buttons.forEach((button) => {
      const isSelected = button === selectedButton;
      button.classList.toggle('is-active', isSelected);
      button.setAttribute('aria-pressed', String(isSelected));
    });

    this.detail.textContent = selectedButton.dataset.detail || '';
  }
}

customElements.define('oishi-promise-pills', OishiPromisePills);

class OishiDiscountForm extends HTMLElement {
  connectedCallback() {
    if (this.dataset.discountReady === 'true') return;
    this.dataset.discountReady = 'true';

    this.form = this.querySelector('[data-discount-form]');
    this.status = this.querySelector('[data-discount-status]');
    this.alert = this.querySelector('[data-discount-alert]');
    this.alertTitle = this.querySelector('[data-discount-alert-title]');
    this.alertMessage = this.querySelector('[data-discount-alert-message]');
    this.alertCloseButtons = Array.from(this.querySelectorAll('[data-discount-alert-close]'));
    this.removeButtons = Array.from(this.querySelectorAll('[data-remove-discount]'));
    this.cartToken = this.dataset.cartToken || '';
    this.activeDiscountCode = this.dataset.activeDiscountCode?.trim() || '';
    this.root = window.Shopify?.routes?.root || '/';
    this.storageKey = 'oishibruu:manual-discount:v2';
    this.pendingStorageKey = 'oishibruu:pending-discount:v1';
    this.legacyStorageKey = 'oishibruu:manual-discount';
    if (!this.form) return;

    this.form.addEventListener('submit', async (event) => {
      event.preventDefault();

      const input = this.form.elements.discount;
      const code = input?.value.trim();
      if (!code) {
        input?.focus();
        return;
      }

      await this.applyDiscount(code);
    });

    this.removeButtons.forEach((button) => {
      button.addEventListener('click', () => this.removeDiscount());
    });

    this.alertCloseButtons.forEach((button) => {
      button.addEventListener('click', () => this.closeDiscountError());
    });

    this.alert?.addEventListener('close', () => {
      this.form.elements.discount?.focus();
    });

    this.resolvePendingDiscount();
    this.syncAppliedDiscount();
  }

  getStoredDiscount() {
    try {
      return JSON.parse(window.sessionStorage.getItem(this.storageKey));
    } catch (_error) {
      return null;
    }
  }

  setStoredDiscount(code, token) {
    try {
      window.sessionStorage.setItem(this.storageKey, JSON.stringify({ code, token }));
    } catch (_error) {
      // Shopify still validates the discount if the browser blocks session storage.
    }
  }

  clearStoredDiscount() {
    try {
      window.sessionStorage.removeItem(this.storageKey);
      window.localStorage.removeItem(this.legacyStorageKey);
    } catch (_error) {
      // The server-side cart update remains authoritative.
    }
  }

  getPendingDiscount() {
    try {
      const pendingDiscount = JSON.parse(window.sessionStorage.getItem(this.pendingStorageKey));
      if (!pendingDiscount?.code || Date.now() - pendingDiscount.createdAt > 5 * 60 * 1000) {
        this.clearPendingDiscount();
        return null;
      }
      return pendingDiscount;
    } catch (_error) {
      return null;
    }
  }

  setPendingDiscount(code) {
    try {
      window.sessionStorage.setItem(this.pendingStorageKey, JSON.stringify({
        code,
        createdAt: Date.now()
      }));
    } catch (_error) {
      // Shopify still validates the code after the manual form submission.
    }
  }

  clearPendingDiscount() {
    try {
      window.sessionStorage.removeItem(this.pendingStorageKey);
    } catch (_error) {
      // There is no pending browser state to clean up when storage is blocked.
    }
  }

  setBusy(isBusy) {
    const submitButton = this.form.querySelector('[type="submit"]');
    if (submitButton) submitButton.disabled = isBusy || Boolean(this.activeDiscountCode);
    this.removeButtons.forEach((button) => {
      button.disabled = isBusy;
    });
    this.form.setAttribute('aria-busy', String(isBusy));
  }

  showDiscountError(title, message) {
    if (this.status) this.status.textContent = message;
    if (!this.alert) return;

    if (this.alertTitle) this.alertTitle.textContent = title;
    if (this.alertMessage) this.alertMessage.textContent = message;

    if (typeof this.alert.showModal === 'function') {
      if (!this.alert.open) this.alert.showModal();
    } else {
      this.alert.setAttribute('open', '');
    }
  }

  closeDiscountError() {
    if (!this.alert) return;

    if (typeof this.alert.close === 'function') {
      this.alert.close();
    } else {
      this.alert.removeAttribute('open');
      this.form.elements.discount?.focus();
    }
  }

  async updateCartDiscount(code) {
    const response = await fetch(`${this.root}cart/update.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ discount: code })
    });

    if (!response.ok) throw new Error('Discount update failed');
    return response.json();
  }

  applyDiscount(code) {
    this.setBusy(true);
    if (this.status) this.status.textContent = 'Applying your discount code…';
    this.setPendingDiscount(code);

    const redirectUrl = new URL(`${this.root}cart`, window.location.origin);
    const previewThemeId = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (previewThemeId) redirectUrl.searchParams.set('preview_theme_id', previewThemeId);
    redirectUrl.searchParams.set('discount_attempt', code);

    const discountUrl = new URL(`${this.root}discount/${encodeURIComponent(code)}`, window.location.origin);
    discountUrl.searchParams.set('redirect', `${redirectUrl.pathname}${redirectUrl.search}`);
    window.location.assign(discountUrl.toString());
  }

  async removeDiscount({ silent = false } = {}) {
    this.setBusy(true);
    if (!silent && this.status) this.status.textContent = 'Removing your discount code…';

    try {
      await this.updateCartDiscount('');
      this.clearStoredDiscount();
      window.location.reload();
    } catch (_error) {
      if (this.status) this.status.textContent = 'We could not remove that code. Please try again.';
      this.setBusy(false);
    }
  }

  resolvePendingDiscount() {
    const currentUrl = new URL(window.location.href);
    const attemptedCode = currentUrl.searchParams.get('discount_attempt');
    const pendingDiscount = this.getPendingDiscount() || (attemptedCode ? { code: attemptedCode } : null);
    if (!pendingDiscount) return;

    this.clearPendingDiscount();
    if (attemptedCode) {
      currentUrl.searchParams.delete('discount_attempt');
      window.history.replaceState({}, '', `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`);
    }
    const wasAccepted =
      this.activeDiscountCode?.toLowerCase() === pendingDiscount.code.toLowerCase();

    if (wasAccepted) {
      this.setStoredDiscount(this.activeDiscountCode, this.cartToken);
      return;
    }

    const input = this.form.elements.discount;
    if (input) input.value = pendingDiscount.code;
    window.requestAnimationFrame(() => {
      this.showDiscountError(
        'Discount code not accepted',
        'That code is not registered in Shopify, has expired, or is not eligible for the items in this cart. Check the code and try again.'
      );
    });
  }

  syncAppliedDiscount() {
    const storedDiscount = this.getStoredDiscount();

    if (!this.activeDiscountCode) {
      if (storedDiscount) this.clearStoredDiscount();
      return;
    }

    const storedCodeMatches =
      storedDiscount?.code?.toLowerCase() === this.activeDiscountCode.toLowerCase();

    // Shopify is authoritative once it returns an active discount. Keep that
    // valid code through reloads even when browser storage is unavailable or
    // Shopify changes the cart token representation between requests.
    if (!storedCodeMatches || storedDiscount?.token !== this.cartToken) {
      this.setStoredDiscount(this.activeDiscountCode, this.cartToken);
    }
  }
}

customElements.define('oishi-discount-form', OishiDiscountForm);

class OishiCartHesitation extends HTMLElement {
  connectedCallback() {
    if (this.dataset.hesitationReady === 'true') return;
    this.dataset.hesitationReady = 'true';

    this.dialog = this.querySelector('[data-hesitation-dialog]');
    this.useButton = this.querySelector('[data-hesitation-use]');
    this.closeButtons = Array.from(this.querySelectorAll('[data-hesitation-close]'));
    this.discountInput = document.querySelector('#CartDiscountCode');
    this.code = this.dataset.discountCode?.trim() || '';
    this.storageKey = `oishibruu:hesitation-offer:v2:${this.code.toLowerCase()}`;
    this.isPreviewMode =
      this.dataset.themeRole !== 'main' ||
      Boolean(window.Shopify?.designMode) ||
      new URLSearchParams(window.location.search).has('preview_theme_id');

    if (!this.dialog || !this.code || !this.discountInput || this.discountInput.disabled) return;
    if (!this.isPreviewMode && this.wasAlreadyShown()) return;

    this.closeButtons.forEach((button) => {
      button.addEventListener('click', () => this.closeOffer());
    });
    this.useButton?.addEventListener('click', () => this.useOffer());

    const delay = Math.max(15, Number(this.dataset.delaySeconds) || 45) * 1000;
    this.timer = window.setTimeout(() => this.showOffer(), delay);
  }

  disconnectedCallback() {
    if (this.timer) window.clearTimeout(this.timer);
  }

  wasAlreadyShown() {
    try {
      return Boolean(window.localStorage.getItem(this.storageKey));
    } catch (_error) {
      return false;
    }
  }

  markAsShown() {
    try {
      window.localStorage.setItem(this.storageKey, JSON.stringify({ shownAt: new Date().toISOString() }));
    } catch (_error) {
      // A blocked storage API can cause the offer to reappear on this device.
    }
  }

  showOffer() {
    if (document.hidden || document.querySelector('dialog[open]') || this.discountInput.disabled) {
      this.timer = window.setTimeout(() => this.showOffer(), 10000);
      return;
    }

    if (!this.isPreviewMode) this.markAsShown();
    if (typeof this.dialog.showModal === 'function') {
      this.dialog.showModal();
    } else {
      this.dialog.setAttribute('open', '');
    }
  }

  closeOffer() {
    if (typeof this.dialog.close === 'function') {
      this.dialog.close();
    } else {
      this.dialog.removeAttribute('open');
    }
  }

  useOffer() {
    this.discountInput.value = this.code;
    this.discountInput.dispatchEvent(new Event('input', { bubbles: true }));
    this.closeOffer();
    this.discountInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
    window.setTimeout(() => {
      this.discountInput.focus();
      this.discountInput.select();
    }, 450);
  }
}

customElements.define('oishi-cart-hesitation', OishiCartHesitation);

class OishiCartRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.dataset.recommendationsReady === 'true') return;
    this.dataset.recommendationsReady = 'true';

    const load = () => this.loadRecommendations();
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        if (!entries[0]?.isIntersecting) return;
        this.observer.disconnect();
        load();
      }, { rootMargin: '0px 0px 300px 0px' });
      this.observer.observe(this);
    } else {
      load();
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  async loadRecommendations() {
    const url = this.dataset.url;
    if (!url) {
      this.remove();
      return;
    }

    try {
      const response = await fetch(url, { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('Recommendations request failed');

      const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
      const recommendations = documentFragment.querySelector('oishi-cart-recommendations');
      if (!recommendations?.innerHTML.trim()) {
        this.remove();
        return;
      }

      this.innerHTML = recommendations.innerHTML;
      this.removeAttribute('data-url');
    } catch (_error) {
      this.remove();
    }
  }
}

customElements.define('oishi-cart-recommendations', OishiCartRecommendations);

class OishiRhythmStory extends HTMLElement {
  connectedCallback() {
    this.initRhythmStory();
  }

  disconnectedCallback() {
    window.clearInterval(this.rhythmTimer);
  }

  initRhythmStory() {
    if (this.dataset.rhythmReady === 'true') return;
    this.dataset.rhythmReady = 'true';
    this.tabs = Array.from(this.querySelectorAll('[data-rhythm-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-rhythm-panel], .infographic-chart-row'));
    this.message = this.querySelector('.infographic-rhythm-story__message');
    const copy = [
      { title: 'Energy', sub: 'How the lift lands', message: 'A quick lift can feel exciting — then disappear just as quickly.', outcome: 'Quick lift. Faster fade.' },
      { title: 'Workday', sub: 'How long it holds', message: 'The upgrade keeps the curve useful deeper into the day.', outcome: 'Steadier rise. Longer rhythm.' },
      { title: 'After-feel', sub: 'What lingers', message: 'A calmer finish means the ritual can stay part of your day, not take it over.', outcome: 'Softer landing. Better balance.' }
    ];

    if (!this.tabs.length) {
      const controls = document.createElement('div');
      controls.className = 'infographic-rhythm-story__controls';
      controls.innerHTML = `<div class="infographic-rhythm-story__tabs" role="tablist" aria-label="Choose a moment in the rhythm">${copy.map((item, index) => `<button class="infographic-rhythm-story__tab${index === 0 ? ' is-active' : ''}" type="button" role="tab" aria-selected="${index === 0}" data-rhythm-tab="${index}" aria-controls="rhythm-panel-${index}" data-rhythm-message="${item.message}" data-rhythm-outcome="${item.outcome}"><strong>${item.title}</strong><small>${item.sub}</small></button>`).join('')}</div>`;
      const legend = this.querySelector('.infographic-comparison__legend');
      legend?.after(controls);
      this.tabs = Array.from(this.querySelectorAll('[data-rhythm-tab]'));
    }

    if (!this.message) {
      this.message = document.createElement('p');
      this.message.className = 'infographic-rhythm-story__message';
      this.message.dataset.rhythmMessage = '';
      this.message.setAttribute('aria-live', 'polite');
      this.querySelector('.infographic-rhythm-story__controls')?.after(this.message);
    }

    this.panels.forEach((panel, index) => {
      panel.dataset.rhythmPanel = String(index);
      panel.id = panel.id || `rhythm-panel-${index}`;
      panel.hidden = index !== 0;
      panel.classList.toggle('is-active', index === 0);
      const header = panel.querySelector('header');
      if (header && !header.querySelector('.infographic-chart-row__copy, p')) {
        const copyNode = document.createElement('p');
        copyNode.className = 'infographic-chart-row__copy';
        copyNode.textContent = ['A fast spark versus a steadier line.', 'Same coffee ritual, more usable runway.', 'A softer landing that keeps the day in balance.'][index] || '';
        header.append(copyNode);
      }
      const chart = panel.querySelector('.infographic-chart');
      if (chart && !chart.querySelector('.infographic-chart__signal')) {
        const signal = document.createElement('div');
        signal.className = 'infographic-chart__signal';
        signal.innerHTML = [['quick spike', 'steadier hold'], ['early peak', 'later support'], ['quick fade', 'balanced finish']][index].map((label) => `<span>${label}</span>`).join('');
        chart.append(signal);
      }
      if (chart && !chart.querySelector('.infographic-chart__legend')) {
        const legend = document.createElement('div');
        legend.className = 'infographic-chart__legend';
        legend.innerHTML = '<span class="infographic-chart__legend-item infographic-chart__legend-item--usual"><i></i> Usual coffee</span><span class="infographic-chart__legend-item infographic-chart__legend-item--upgrade"><i></i> OishiBruu</span>';
        chart.prepend(legend);
      }
      panel.querySelectorAll('.infographic-linechart__label').forEach((label) => label.setAttribute('aria-hidden', 'true'));
    });

    this.tabs.forEach((tab, index) => {
      tab.setAttribute('aria-controls', this.panels[index]?.id || `rhythm-panel-${index}`);
      tab.addEventListener('click', () => {
        this.showRhythm(index);
        this.startRhythm();
      });
    });
    this.showRhythm(0);
    this.startRhythm();
  }

  startRhythm() {
    window.clearInterval(this.rhythmTimer);
    this.rhythmTimer = window.setInterval(() => this.showRhythm((this.index + 1) % this.panels.length), 5000);
  }

  showRhythm(index) {
    this.index = index;
    this.panels.forEach((panel, panelIndex) => {
      const active = panelIndex === index;
      panel.hidden = !active;
      panel.classList.toggle('is-active', active);
    });
    this.tabs.forEach((tab, tabIndex) => {
      const active = tabIndex === index;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', String(active));
    });
    const activeTab = this.tabs[index];
    if (this.message && activeTab) this.message.textContent = activeTab.dataset.rhythmMessage || '';
  }
}

customElements.define('oishi-rhythm-story', OishiRhythmStory);

class OishiCollectionSwitcher extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[data-collection-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-collection-panel]'));
    this.tabList = this.querySelector('.collection-catalog-tabs');
    this.resultCount = this.closest('[data-collection-filters]')?.querySelector('[data-result-count]');
    if (!this.tabs.length || !this.panels.length) return;

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.show(tab.dataset.collectionTab));
      tab.addEventListener('keydown', (event) => this.handleKeydown(event));
    });

    this.updateStickyState = () => {
      if (!this.tabList) return;
      const stickyTop = Number.parseFloat(window.getComputedStyle(this.tabList).top) || 0;
      const isStuck = window.scrollY > 0 && this.tabList.getBoundingClientRect().top <= stickyTop + 1;
      this.tabList.classList.toggle('is-stuck', isStuck);
    };
    window.addEventListener('scroll', this.updateStickyState, { passive: true });
    window.addEventListener('resize', this.updateStickyState);
    window.requestAnimationFrame(this.updateStickyState);
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.updateStickyState);
    window.removeEventListener('resize', this.updateStickyState);
  }

  handleKeydown(event) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const enabledTabs = this.tabs.filter((tab) => !tab.disabled);
    const currentIndex = enabledTabs.indexOf(event.currentTarget);
    let nextIndex = currentIndex;
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % enabledTabs.length;
    if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + enabledTabs.length) % enabledTabs.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = enabledTabs.length - 1;
    const nextTab = enabledTabs[nextIndex];
    this.show(nextTab.dataset.collectionTab);
    nextTab.focus();
  }

  show(format) {
    let activeTab;
    this.tabs.forEach((tab) => {
      const active = tab.dataset.collectionTab === format;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', String(active));
      tab.setAttribute('tabindex', active ? '0' : '-1');
      if (active) activeTab = tab;
    });
    this.panels.forEach((panel) => {
      const active = panel.dataset.collectionPanel === format;
      panel.classList.toggle('is-active', active);
      panel.hidden = !active;
    });

    if (this.resultCount && activeTab) {
      const count = Number(activeTab.dataset.productCount) || 0;
      this.resultCount.textContent = `${count} ${count === 1 ? 'product' : 'products'}`;
    }
    window.requestAnimationFrame(this.updateStickyState);
  }
}

customElements.define('oishi-collection-switcher', OishiCollectionSwitcher);

class OishiCollectionFilters extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('[data-collection-filter-trigger]');
    this.layer = this.querySelector('[data-collection-filter-layer]');
    this.panel = this.querySelector('[data-collection-filter-panel]');
    this.closeControls = Array.from(this.querySelectorAll('[data-collection-filter-close]'));
    this.sort = this.querySelector('[data-collection-sort]');

    this.sort?.addEventListener('change', () => this.sort.form?.requestSubmit());
    if (!this.trigger || !this.layer || !this.panel) return;

    this.handleKeydown = this.handleKeydown.bind(this);
    this.trigger.addEventListener('click', () => this.openFilters());
    this.closeControls.forEach((control) => control.addEventListener('click', () => this.closeFilters()));
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.handleKeydown);
    document.body.classList.remove('collection-filters-open');
  }

  openFilters() {
    this.previouslyFocused = document.activeElement;
    this.layer.classList.add('is-open');
    this.trigger.setAttribute('aria-expanded', 'true');
    document.body.classList.add('collection-filters-open');
    document.addEventListener('keydown', this.handleKeydown);
    window.requestAnimationFrame(() => this.panel.querySelector('[data-collection-filter-close]')?.focus());
  }

  closeFilters() {
    this.layer.classList.remove('is-open');
    this.trigger.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('collection-filters-open');
    document.removeEventListener('keydown', this.handleKeydown);
    if (this.previouslyFocused instanceof HTMLElement) this.previouslyFocused.focus();
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeFilters();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = Array.from(this.panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'))
      .filter((element) => element.getClientRects().length);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

customElements.define('oishi-collection-filters', OishiCollectionFilters);

document.addEventListener('DOMContentLoaded', () => {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isMobileViewport = window.matchMedia('(max-width: 749px)').matches;
  const startDecorativeVideo = (video) => {
    if (!video || video.dataset.playbackStarted === 'true') return;
    video.dataset.playbackStarted = 'true';
    video.play().catch(() => {
      delete video.dataset.playbackStarted;
    });
  };

  if (!prefersReducedMotion) {
    const heroVideo = document.querySelector('.hero__media video');
    if (heroVideo && !isMobileViewport) {
      const startHeroVideo = () => {
        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(() => startDecorativeVideo(heroVideo), { timeout: 1800 });
        } else {
          window.setTimeout(() => startDecorativeVideo(heroVideo), 800);
        }
      };
      if (document.readyState === 'complete') startHeroVideo();
      else window.addEventListener('load', startHeroVideo, { once: true });
    }

    const comparisonVideos = document.querySelectorAll('.comparison-video__media video');
    if ('IntersectionObserver' in window) {
      const videoObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          startDecorativeVideo(entry.target);
          observer.unobserve(entry.target);
        });
      }, { rootMargin: '500px 0px' });
      comparisonVideos.forEach((video) => videoObserver.observe(video));
    } else {
      comparisonVideos.forEach(startDecorativeVideo);
    }
  }

  const backToTop = document.querySelector('[data-back-to-top]');
  if (backToTop) {
    const toggleBackToTop = () => { backToTop.hidden = window.scrollY < 480; };
    backToTop.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
    toggleBackToTop();
    window.addEventListener('scroll', toggleBackToTop, { passive: true });
  }

  document.querySelectorAll('[data-product-form]').forEach((form) => {
    const select = form.querySelector('select[name="id"]');
    const submit = form.querySelector('[data-product-submit]');
    if (!select || !submit) return;
    const updateAvailability = () => {
      const available = select.selectedOptions[0]?.dataset.available === 'true';
      submit.disabled = !available;
      submit.textContent = available ? 'Add to cart' : 'Sold out';
    };
    select.addEventListener('change', updateAvailability);
    updateAvailability();
  });

  const header = document.querySelector('[data-sticky-header]');
  if (!header) return;

  const setScrolled = () => {
    header.classList.toggle('is-scrolled', window.scrollY > 16);
  };

  setScrolled();
  window.addEventListener('scroll', setScrolled, { passive: true });
});
